import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Organization } from "../../../services/organization/organizationModel";
import {
  PremiumSponsorship,
  PremiumSponsorshipStatus,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import { countAvailableSponsorshipCredits } from "../../../services/premiumSponsorship/premiumSponsorshipService";

const PremiumSponsorshipsOverview = ({
  organization,
  sponsorships,
  onInvite,
}: {
  organization: Organization;
  sponsorships: PremiumSponsorship[];
  onInvite: () => void;
}) => {
  const { t } = useTranslation(["organization"]);

  const counts = useMemo(
    () =>
      sponsorships.reduce(
        (acc, sponsorship) => {
          acc[sponsorship.status] += 1;
          return acc;
        },
        {
          invited: 0,
          accepted: 0,
          declined: 0,
          revoked: 0,
        } as Record<PremiumSponsorshipStatus, number>,
      ),
    [sponsorships],
  );

  const availableCredits = useMemo(
    () => countAvailableSponsorshipCredits(organization, sponsorships),
    [organization, sponsorships],
  );

  // Anneau de crédits : part consommée du quota (maxSponsorshipsCount), 252 = circonférence du cercle (r = 40).
  const maxCredits = organization.maxSponsorshipsCount;
  const ring =
    maxCredits > 0
      ? Math.min(Math.max(availableCredits / maxCredits, 0), 1) * 252
      : 0;

  const tiles = [
    {
      key: "accepted",
      label: "organization:premium-sponsorship.overview.ACCEPTED",
    },
    {
      key: "declined",
      label: "organization:premium-sponsorship.overview.DECLINED",
    },
    {
      key: "invited",
      label: "organization:premium-sponsorship.overview.INVITED",
    },
    {
      key: "revoked",
      label: "organization:premium-sponsorship.overview.REVOKED",
    },
  ] as const;

  return (
    <div className="sp-overview">
      <div className="ui-kpi sp-credits">
        <svg
          className="sp-credits_ring"
          viewBox="0 0 100 100"
          aria-hidden="true"
        >
          <circle cx="50" cy="50" r="40" className="sp-credits_track" />
          <circle
            cx="50"
            cy="50"
            r="40"
            className="sp-credits_arc"
            strokeDasharray={`${ring.toFixed(1)} 252`}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <span className="ui-kpi_l">
          {t("organization:premium-sponsorship.CREDITS_LEFT")}
        </span>
        <b className="ui-kpi_v">
          {availableCredits}
          <span className="sp-credits_max"> / {maxCredits}</span>
        </b>
        <button className="btn--white --btn--s" onClick={onInvite}>
          {t("organization:premium-sponsorship.INVITE_BUTTON")}
        </button>
      </div>

      {tiles.map(({ key, label }) => (
        <div className="ui-kpi" key={key}>
          <span className="ui-kpi_l">
            <i className={`sp-dot --${key}`} />
            {t(label)}
          </span>
          <b className="ui-kpi_v">{counts[key]}</b>
        </div>
      ))}
    </div>
  );
};

export default PremiumSponsorshipsOverview;
